import { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, Container, Paper, IconButton, InputAdornment, Link,
  FormControlLabel, Checkbox,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { authApi } from '../services/api'
import { loginSuccess } from '../store/authSlice'
import { AppDispatch } from '../store'

interface SignupPageProps {
  onSwitchToLogin: () => void
}

const SignupPage = ({ onSwitchToLogin }: SignupPageProps) => {
  const [form, setForm] = useState({ name: '', company: '', email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [noOrganisation, setNoOrganisation] = useState(false)
  const [pendingApproval, setPendingApproval] = useState<{ message: string; tenantName: string } | null>(null)

  const dispatch = useDispatch<AppDispatch>()

  const FREE_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
    'live.com', 'msn.com', 'me.com', 'gmx.com', 'inbox.com',
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const emailDomain = form.email.split('@')[1]?.toLowerCase()
    if (emailDomain && FREE_EMAIL_DOMAINS.includes(emailDomain)) {
      setError('Please use your work email address. Free email providers are not accepted.')
      return
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        company: form.company || form.email.split('@')[1]?.split('.')[0] || 'My Company',
        terms_accepted: true,
        terms_version: 'v1',
      }
      const res = await authApi.signup(payload)
      const data = res.data

      if (data.status === 'verification_sent' || data.status === 'verification_pending') {
        setPendingApproval({ message: data.message, tenantName: '' })
        return
      }

      if (data.status === 'pending_approval') {
        setPendingApproval({ message: data.message, tenantName: data.tenant_name })
        return
      }

      const { access_token, refresh_token, user } = data
      localStorage.setItem('token', access_token)
      localStorage.setItem('refreshToken', refresh_token)
      localStorage.setItem('user', JSON.stringify(user))
      dispatch(loginSuccess({ token: access_token, user }))
    } catch (err: any) {
      const detail = err.response?.data?.detail
      // 403 here means the email domain matches no existing organisation, so
      // there is no account to join and self-service tenant creation is
      // closed. That is not a validation failure the user can fix by editing
      // the form — offering a demo is the only useful next step, so it gets a
      // distinct state rather than a red error they will read as their mistake.
      if (err.response?.status === 403) {
        setNoOrganisation(true)
        setError('')
      } else if (typeof detail === 'string') {
        setError(detail)
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg || d).join('. '))
      } else {
        setError('Unable to create account. Please check your details and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const updated = { ...form, [field]: value }
    // Auto-suggest company name from email domain
    if (field === 'email' && !form.company) {
      const domain = value.split('@')[1]
      if (domain && !FREE_EMAIL_DOMAINS.includes(domain.toLowerCase())) {
        const companyName = domain.split('.')[0]
        updated.company = companyName.charAt(0).toUpperCase() + companyName.slice(1)
      }
    }
    setForm(updated)
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, letterSpacing: 2 }}>
              ARIAS
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
              Join your organisation
            </Typography>
            {/* Said before they fill anything in, not after. Accounts are
                created for organisations that already use ARIAS, and someone
                whose company does not will otherwise type out a full form to
                be told no at the end. */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              For people whose organisation already uses ARIAS. Use your work
              email and we will match you to your team's account.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              New to ARIAS? <Link href="/request-demo">Request a demo</Link>.
            </Typography>
          </Box>

          {pendingApproval && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{pendingApproval.tenantName}</Typography>
              {pendingApproval.message}
              <Box sx={{ mt: 1 }}>
                <Link component="button" variant="body2" onClick={onSwitchToLogin}>Back to sign in</Link>
              </Box>
            </Alert>
          )}

          {!pendingApproval && <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Create Account
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              {noOrganisation && (
                <Alert severity="info" sx={{ mb: 2 }}
                       action={
                         <Button color="inherit" size="small"
                                 href="/request-demo">
                           Request a demo
                         </Button>
                       }>
                  We don't recognise your organisation yet, so there's no account
                  to join. Request a demo and we'll get you set up.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth label="Full Name" value={form.name}
                  onChange={handleChange('name')} margin="normal" required autoFocus
                />
                <TextField
                  fullWidth label="Company Name" value={form.company}
                  onChange={handleChange('company')} margin="normal" required
                />
                <TextField
                  fullWidth label="Work Email" type="email" value={form.email}
                  onChange={handleChange('email')} margin="normal" required
                />
                <TextField
                  fullWidth label="Password" value={form.password}
                  onChange={handleChange('password')} margin="normal" required
                  type={showPassword ? 'text' : 'password'}
                  helperText="Minimum 8 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  control={<Checkbox checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} />}
                  label={
                    <Typography variant="body2">
                      I agree to the{' '}
                      <Link href="/terms" target="_blank" rel="noopener">Terms and Conditions</Link>
                    </Typography>
                  }
                  sx={{ mt: 2 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                  By creating an account, you also acknowledge our{' '}
                  <Link href="/privacy" target="_blank" rel="noopener">Privacy Statement</Link>.
                </Typography>
                <Button
                  type="submit" fullWidth variant="contained" disabled={loading || !acceptedTerms}
                  sx={{ mt: 2, mb: 2 }}
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                Already have an account?{' '}
                <Link component="button" variant="body2" onClick={onSwitchToLogin} sx={{ cursor: 'pointer' }}>
                  Sign in
                </Link>
              </Typography>
            </CardContent>
          </Card>}
        </Paper>
      </Box>
    </Container>
  )
}

export default SignupPage
